import { getAPIURL } from "./config";

/**
 * Получить список проектов для согласования
 * @param {number} projectId - Опциональный ID проекта для фильтрации
 * @returns {Promise<Array>} Массив проектов для согласования
 */
export const getProjectApprovals = async (projectId = null) => {
  try {
    let url = `${getAPIURL()}project_estimates/my_approvals`;
    
    if (projectId) {
      url += `?project_id=${projectId}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Ответ сервера: ${response.status} ${response.statusText}`);
      console.error(`Тело ответа: ${errorText}`);
      throw new Error(
        `Ошибка при получении проектов для согласования: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Ошибка при получении проектов для согласования:", error);
    throw error;
  }
};

/**
 * Согласовать или отклонить шаг согласования сметы
 * PATCH /project_estimates/projects/{project_id}/approvals/steps/{step_id}
 * @param {number} projectId - ID проекта
 * @param {number} stepId - ID шага согласования
 * @param {{ status: string, comment?: string }} body
 * @returns {Promise<Object>}
 */
export const decideProjectEstimateApprovalStep = async (
  projectId,
  stepId,
  body,
) => {
  try {
    const response = await fetch(
      `${getAPIURL()}project_estimates/projects/${projectId}/approvals/steps/${stepId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      const text = await response.text();
      let detail = text;
      try {
        const json = JSON.parse(text);
        if (Array.isArray(json.detail)) {
          detail = json.detail
            .map((d) => {
              const loc = Array.isArray(d.loc) ? d.loc.join(".") : "";
              const msg = d.msg != null ? d.msg : String(d);
              return loc ? `${loc}: ${msg}` : msg;
            })
            .join("; ");
        } else if (typeof json.detail === "string") {
          detail = json.detail;
        } else if (json.message) {
          detail = json.message;
        }
      } catch (_) {}
      throw new Error(detail);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при решении по шагу согласования сметы:", error);
    throw error;
  }
};

/**
 * @deprecated Используйте decideProjectEstimateApprovalStep
 * Изменить статус согласования проекта
 * @param {number} projectId - ID проекта
 * @param {number} approvalId - ID согласования
 * @param {string} status - Статус: "согласован" или "не согласован"
 * @returns {Promise<Object>} Результат изменения статуса
 */
export const changeApprovalStatus = async (projectId, approvalId, status) => {
  try {
    const url = `${getAPIURL()}project_estimates/projects/${projectId}/my_approvals/${approvalId}/${status}`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Ответ сервера: ${response.status} ${response.statusText}`);
      console.error(`Тело ответа: ${errorText}`);
      throw new Error(
        `Ошибка при изменении статуса согласования: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при изменении статуса согласования:", error);
    throw error;
  }
};

/**
 * Отменить отправку на согласование
 * @param {number} projectId - ID проекта
 * @returns {Promise<Object>} Результат отмены согласования
 */
export const cancelApprovalRequest = async (projectId) => {
  try {
    const url = `${getAPIURL()}project_estimates/projects/${projectId}/cancel_approval_request`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Ответ сервера: ${response.status} ${response.statusText}`);
      console.error(`Тело ответа: ${errorText}`);
      throw new Error(
        `Ошибка при отмене согласования: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при отмене согласования:", error);
    throw error;
  }
};

/**
 * Получить статусы согласования проекта
 * @param {number} projectId - ID проекта
 * @returns {Promise<Array>} Массив согласований с статусами
 */
export const getProjectApprovalStatuses = async (projectId) => {
  try {
    const url = `${getAPIURL()}project_estimates/projects/${projectId}/approvals`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Ответ сервера: ${response.status} ${response.statusText}`);
      console.error(`Тело ответа: ${errorText}`);
      throw new Error(
        `Ошибка при получении статусов согласования: ${response.status} ${response.statusText}`
      );
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Ошибка при получении статусов согласования:", error);
    throw error;
  }
};

